/**
 * The wheel: modular arithmetic, played rather than defined.
 *
 * A dial with some number of places round it. You hop forward by the same
 * amount over and over, and when you run off the end you carry on from the
 * start — which is the whole of "mod", and is also just how a clock works.
 *
 * What makes it worth playing rather than reading is the pattern it leaves.
 * Hopping by 5 on a wheel of 12 eventually touches every single place; hopping
 * by 3 on the same wheel only ever touches four of them and comes home early.
 * The reason is the highest common factor of the hop and the wheel, and a
 * child can find that out entirely by trying it — no definition required.
 */

export function gcd(a: number, b: number): number {
  let x = Math.abs(Math.floor(a));
  let y = Math.abs(Math.floor(b));
  while (y !== 0) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x;
}

/** Where you are after this many hops. Always a real place on the wheel. */
export function positionAfter(hops: number, step: number, size: number): number {
  const places = Math.max(1, Math.floor(size));
  const total = Math.max(0, Math.floor(hops)) * Math.floor(step);
  // Positive remainder even if somebody hands us a backwards hop.
  return ((total % places) + places) % places;
}

/** Every place landed on, in order, starting from where you began. */
export function landings(hops: number, step: number, size: number): number[] {
  return Array.from({ length: Math.max(0, Math.floor(hops)) + 1 }, (_, i) => positionAfter(i, step, size));
}

/**
 * How many hops it takes to get back to the start. This is the number a child
 * discovers by going round, and it is the wheel divided by the highest common
 * factor — which is why hopping by 6 on a wheel of 12 is home in two.
 */
export function cycleLength(step: number, size: number): number {
  const places = Math.max(1, Math.floor(size));
  const hop = ((Math.floor(step) % places) + places) % places;
  if (hop === 0) return 1;
  return places / gcd(hop, places);
}

/** Every place this hop can ever reach, in order round the wheel. */
export function reachable(step: number, size: number): number[] {
  const places = Math.max(1, Math.floor(size));
  const common = gcd(((Math.floor(step) % places) + places) % places || places, places);
  const out: number[] = [];
  for (let p = 0; p < places; p += common) out.push(p);
  return out;
}

/** True when the hop eventually visits every single place. */
export function visitsEverywhere(step: number, size: number): boolean {
  return reachable(step, size).length === Math.max(1, Math.floor(size));
}

/** What the wheel says about where you are, in plain words. */
export function wheelLine(hops: number, step: number, size: number): string {
  const total = Math.max(0, Math.floor(hops)) * Math.floor(step);
  const at = positionAfter(hops, step, size);
  if (total === 0) return `start at 0 on a wheel of ${size}`;
  return `${total} on a wheel of ${size} lands on ${at}`;
}
